import { createSlice } from '@reduxjs/toolkit';
import { UserInfoItem } from '@tencent/wemeet-websdk-next';
import { hasScreen, hasStream, isUserVideoOn } from '@utils/helper/media';
import { findFirstVideoOpenOrScreenOpenUser } from '@utils/helper/user';
import { userListSortHelper } from '@utils/hooks/useUserList';
import { MeetingDisplayMode } from 'src/type/meeting';
import { AppState } from '../index';

/**
 * studio model 层
 *
 *
 *
 */

interface StudioState {
  /** @description 是否展示参会成员列表 */
  meetingDisplayMode: MeetingDisplayMode;
  /** @description 是否锁定大视频 */
  isLockedBigVideo: boolean;
  /** @description 大视频信息 */
  bigVideoData: UserInfoItem | undefined;
}

// 初始状态
const initialState: StudioState = {
  meetingDisplayMode: MeetingDisplayMode.PureAudio,
  isLockedBigVideo: false,
  bigVideoData: undefined,
};

export const studioSlice = createSlice({
  name: 'studio',
  initialState,
  reducers: {
    /**
     * 设置大画面和展示模式
     *
     * 1. 展示模式： 遍历用户列表，如果有用户 up_video_on 是开的就是演讲者模式。
     *  和 setMeetingDisplayMode 重复了
     * 2. 如果是演讲者模式
     *  2.1 且没有锁定大画面
     *  2.2 且目前的大画面没有视频流
     *  2.3 则从用户列表里找第一个有打开视频的用户，设置成大画面
     * 3. 如果是纯音频模式
     *  3.1 大画面设置成 undefined
     * @param state
     * @param action
     */
    setBigVideoAndDisplayModeFromUserList: (state, action) => {
      const beforeOrderUserList: UserInfoItem[] = action.payload;
      // userList 要先排序
      const userList = userListSortHelper(beforeOrderUserList);
      /** @description 当前大视频用户信息 */
      const oldBigVideoData = state.bigVideoData;

      // 理论上排序后的数组第一个就是当前列表第一个开视频的，或者开共享的，然后再对列表第一个对象做一个判断，即可得到下面的mode，
      // 马上产品体验了，关键逻辑，先不动他
      /** @description 新的userlist中的第一个开视频或者开共享的用户 */
      const bigVideoUser = findFirstVideoOpenOrScreenOpenUser(userList);
      // 如果有符合条件的大视频用户，则就是演讲者模式，否则就是纯音频
      const mode = bigVideoUser ? MeetingDisplayMode.Lecturer : MeetingDisplayMode.PureAudio;

      state.meetingDisplayMode = mode;
      if (mode === MeetingDisplayMode.PureAudio) {
        state.isLockedBigVideo = false;
        state.bigVideoData = undefined;
        return;
      }

      // 演讲者模式
      // 如果老的大视频数据存在，就说明一直是演讲者模式，那就看看是否要更新
      if (oldBigVideoData) {
        // step1 在新的userlist找到老的大视频数据，看看是否有变化
        /** @description userList中的新的当前大画面对象 */
        let updatedOldBigVideoData;
        // 从新的列表中找到当前大视频用户，判断一下，是不是关视频了
        for (const user of userList) {
          if (
            user?.id?.['@app_uid'] === oldBigVideoData?.id?.['@app_uid'] &&
            user?.rtcStream?.streamType === oldBigVideoData?.rtcStream?.streamType
          ) {
            updatedOldBigVideoData = user;
            break;
          }
        }

        /** @description 老的大视频数据和更新后的大视频数据，两边视频都是开的, 并且更新后的大视频不是屏幕共享 */
        const isOldAndNewBigVideoOpen =
          updatedOldBigVideoData &&
          isUserVideoOn(oldBigVideoData) &&
          isUserVideoOn(updatedOldBigVideoData) &&
          !hasScreen(updatedOldBigVideoData);
        /** @description 老的大视频数据和更新后的大视频数据，两边屏幕共享都是开的 */
        const isOldAndNewBigScreenShare =
          updatedOldBigVideoData && hasScreen(oldBigVideoData) && hasScreen(updatedOldBigVideoData);

        // 原先大画面数据不符合大画面条件，则重选一个，用新的替换他。
        if (!(isOldAndNewBigVideoOpen || isOldAndNewBigScreenShare)) {
          state.isLockedBigVideo = false;
          // 既然原来视频关了，我们就把新的赋值给他
          state.bigVideoData = bigVideoUser;
        } else if (hasScreen(bigVideoUser as UserInfoItem) && !state.isLockedBigVideo) {
          // 原先大画面数据符合条件，那就看看新的是不是屏幕共享，屏幕共享需要被顶到第一个
          state.bigVideoData = bigVideoUser;
        }
      } else {
        // 老的大视频无数据，则设置为新的
        state.bigVideoData = bigVideoUser;
      }
    },
    /**
     * 设置 studio 展示模式
     * MeetingDisplayMode
     *
     * 1. 遍历用户列表，如果有用户 up_video_on 是开的就是演讲者模式
     *
     * 目前没有地方调用到该方法
     *
     * @param state
     * @param action
     */
    setMeetingDisplayMode: (state, action) => {
      const userList = action.payload;
      // 找到用户列表中是否有开启视频的用户，
      // TODO 抽离工具方法， sdk 静态方法
      state.meetingDisplayMode = userList.some((user: UserInfoItem) => isUserVideoOn(user) && hasStream(user))
        ? MeetingDisplayMode.Lecturer
        : MeetingDisplayMode.PureAudio;
    },
    /**
     * 更新大画面
     *
     * 目前只有点击小画面的时候会调用
     * @param state
     * @param action
     */
    updateBigVideo: (state, action) => {
      const user: UserInfoItem = action.payload;

      // // 先把选中的设置为大流, 本地流不需要切换大小流
      // if (user.rtcStream?.isLocal === false) {
      //   wemeetClient.trtcClient.setRemoteVideoStreamType(user.rtcStream?.stream as RemoteStream, 'big');
      // }

      // // 把当前的bigVideo设为小流，本地流不需要切换大小流
      // if (state.bigVideoData?.rtcStream?.stream && state.bigVideoData?.rtcStream.isLocal === false) {
      //   console.log('big:', state.bigVideoData?.rtcStream?.stream);
      //   wemeetClient.trtcClient.setRemoteVideoStreamType(
      //     state.bigVideoData?.rtcStream?.stream as RemoteStream,
      //     'small',
      //   );
      // }

      // 切换bigVideo
      state.bigVideoData = user;

      // 锁定bigVideo
      state.isLockedBigVideo = true;
    },
  },
});

export const { setMeetingDisplayMode, setBigVideoAndDisplayModeFromUserList, updateBigVideo } = studioSlice.actions;

export const getMeetingDisplayMode = (state: AppState) => state.studio.meetingDisplayMode;

export const getBigVideoData = (state: AppState) => state.studio.bigVideoData;

export default studioSlice.reducer;
